/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import PageHeader from '../../customs/DiviPage/PageHeader/PageHeader';
import { DiviNavigationIds } from '../../../models/diviProgress';
import PageFooter from '../../customs/DiviPage/PageFooter/PageFooter';
import DiviPage from '../../customs/DiviPage/DiviPage';
import { NodeType } from '../../../backendModels/report.model';
import { getPageTitle, PageType } from '../../../models/diviPage';
import { BaseNodeRecordValueTypeMap } from '../../../backendModels/records.model';
import Participants from './Participants/Participants';
import SymptomStart from './SymptomStart/SymptomStart';
import MissionType from './MissionType/Type';
import MissionLocation from './MissionLocation/Location';
import Destination from './Destination/Destination';
import { useCSS } from '../../../provider/CSSProvider';
import style from './OperationalData.scss?inline';
import { Typography } from '@mui/material';
import {
  OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY,
  OperationalDataTransportLocationRecord,
  TransportLocationPropertyKeys,
} from '../../../models/genericElements/operationalData/transportLocation';
import {
  LocationPropertyKeys,
  mapStringToMissionLocationKey,
  OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY,
  OperationalDataLocationRecord,
} from '../../../models/genericElements/operationalData/missionLocation';
import {
  OPERATIONAL_DATA_PARTICIPANTS_RECORD_ELEMENT_KEY,
  OperationalDataParticipantsRecord,
  ParticipantsPropertyKeys,
} from '../../../models/genericElements/operationalData/participants';
import { DataImportDialog } from '../../customs/ImportDialog/DataImportDialog';
import { useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { ReportsAPIContext } from '../../../provider/ReportsAPIProvider';
import { localizeOperationalPropertyTuple, NormalizedMission, normalizeMissionData } from '../../../models/operational';
import { Draft } from 'immer';
import { InputState } from '../../../backendModels/general.model';
import {
  handleUntouchedMultiSelectFieldUpdates,
  handleUntouchedSingleSelectFieldUpdates,
  handleUntouchedTextFieldUpdates,
  overwriteSingleSelectField,
  overwriteTextField,
} from '../../../utils/recordWithTouched';
import { isGenericRecordDeletable } from '../../../models/generic';
import {
  SYMPTOM_ONSET_RECORD_ELEMENT_KEY,
  SymptomOnsetPropertyKeys,
  SymptomOnsetRecord,
} from '../../../models/genericElements/operationalData/symptomOnset';
import { localizePatientPropertyTuple, NormalizedPatient, normalizePatient } from '../../../models/patient';
import { calculateMissionDataDiff, calculatePatientDiff, compareByDiviOrder } from './operationalData.utils';
import { hasAnyMissionDataDiff, hasMissionDataDiff, hasPatientDataDiff } from '../../../utils/dataDiff';
import { ApplicationConfig } from '../../../config/application.config';

export function OperationalData() {
  useCSS(style);

  const recordTypesOfPage = [] as (keyof BaseNodeRecordValueTypeMap)[];
  const nodeTypeOfPage = NodeType.METADATA;
  const elementKeysOfPage: string[] = [
    OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY,
    OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY,
    OPERATIONAL_DATA_PARTICIPANTS_RECORD_ELEMENT_KEY,
    SYMPTOM_ONSET_RECORD_ELEMENT_KEY,
  ];

  const {
    findNodes,
    adaptRecord,
    missionData,
    patient,
    findRecordOrDefault,
    lastSynchronizedBaseData,
    setLastSynchronizedBaseData,
    lastSynchronizedMissionData,
    setLastSynchronizedMissionData,
  } = useContext(ReportsAPIContext);

  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  const operationalTransportRecord = findRecordOrDefault(
    'generic',
    nodeTypeOfPage,
    OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY
  ) as OperationalDataTransportLocationRecord;

  const operationalLocationRecord = findRecordOrDefault(
    'generic',
    nodeTypeOfPage,
    OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY
  ) as OperationalDataLocationRecord;

  const operationalParticipantsRecord = findRecordOrDefault(
    'generic',
    nodeTypeOfPage,
    OPERATIONAL_DATA_PARTICIPANTS_RECORD_ELEMENT_KEY
  ) as OperationalDataParticipantsRecord;

  const symptomOnsetRecord = findRecordOrDefault(
    'generic',
    nodeTypeOfPage,
    SYMPTOM_ONSET_RECORD_ELEMENT_KEY
  ) as SymptomOnsetRecord;

  const patientDataDiff = useMemo(() => {
    return calculatePatientDiff(patient, lastSynchronizedBaseData?.patient);
  }, [patient, lastSynchronizedBaseData]);

  const missionDataDiff = useMemo(() => {
    return calculateMissionDataDiff(missionData, lastSynchronizedMissionData?.mission);
  }, [missionData, lastSynchronizedMissionData]);

  const localizedOperationalDataDiff = useMemo(() => {
    missionDataDiff.sort(compareByDiviOrder);
    patientDataDiff.sort(compareByDiviOrder);

    const missionDiff = missionDataDiff.map((val) => {
      return localizeOperationalPropertyTuple(val[0] as keyof NormalizedMission, val[1], {
        operationalTransportRecord,
        operationalLocationRecord,
        operationalParticipantsRecord,
      });
    });

    const patientDiff = patientDataDiff.map((val) => {
      return localizePatientPropertyTuple(val[0] as keyof NormalizedPatient, val[1], { symptomOnsetRecord });
    });

    return [...patientDiff, ...missionDiff];
  }, [
    missionDataDiff,
    patientDataDiff,
    operationalTransportRecord,
    operationalLocationRecord,
    operationalParticipantsRecord,
    symptomOnsetRecord,
  ]);

  useEffect(() => {
    setIsImportDialogOpen(missionDataDiff.length > 0 || patientDataDiff.length > 0);
  }, [missionDataDiff, patientDataDiff]);

  useEffect(() => {
    function saveParticipants(onSuccess: (normalizedMission: NormalizedMission) => void) {
      if (missionData?.participants != null && findNodes(nodeTypeOfPage).length > 0) {
        adaptRecord(
          'generic',
          nodeTypeOfPage,
          (draft: Draft<OperationalDataParticipantsRecord>, deleteRecord) => {
            draft.inputState = InputState.ENTERED;

            const synchronizedDataUpdate: NormalizedMission = {};
            handleUntouchedMultiSelectFieldUpdates(
              draft,
              ParticipantsPropertyKeys.participants,
              missionData?.participants,
              synchronizedDataUpdate
            );

            if (isGenericRecordDeletable(draft)) {
              deleteRecord();
            }
            onSuccess(synchronizedDataUpdate);
          },
          OPERATIONAL_DATA_PARTICIPANTS_RECORD_ELEMENT_KEY
        );
      }
    }

    function saveMissionLocation(onSuccess: (normalizedMission: NormalizedMission) => void) {
      if (missionData?.missionLocation != null && findNodes(nodeTypeOfPage).length > 0) {
        adaptRecord(
          'generic',
          nodeTypeOfPage,
          (draft: Draft<OperationalDataLocationRecord>, deleteRecord) => {
            draft.inputState = InputState.ENTERED;

            const synchronizedDataUpdate: NormalizedMission = {};
            handleUntouchedTextFieldUpdates(
              draft,
              LocationPropertyKeys.missionStreetGPSLatitude,
              missionData.missionLocation?.address?.street,
              synchronizedDataUpdate
            );
            handleUntouchedTextFieldUpdates(
              draft,
              LocationPropertyKeys.missionCityGPSLongitude,
              missionData.missionLocation?.address?.city,
              synchronizedDataUpdate
            );
            handleUntouchedTextFieldUpdates(
              draft,
              LocationPropertyKeys.missionPostcode,
              missionData.missionLocation?.address?.postalCode,
              synchronizedDataUpdate
            );

            handleUntouchedSingleSelectFieldUpdates(
              draft,
              LocationPropertyKeys.missionLocationType,
              mapStringToMissionLocationKey(missionData.missionLocation?.type),
              synchronizedDataUpdate
            );

            if (isGenericRecordDeletable(draft)) {
              deleteRecord();
            }

            onSuccess(synchronizedDataUpdate);
          },
          OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY
        );
      }
    }

    function saveTransportDestination(onSuccess: (normalizedMission: NormalizedMission) => void) {
      if (missionData?.transportDestination != null && findNodes(nodeTypeOfPage).length > 0) {
        adaptRecord(
          'generic',
          nodeTypeOfPage,
          (draft: Draft<OperationalDataTransportLocationRecord>, deleteRecord) => {
            draft.inputState = InputState.ENTERED;

            const synchronizedDataUpdate: NormalizedMission = {};
            handleUntouchedTextFieldUpdates(
              draft,
              TransportLocationPropertyKeys.transportLocation,
              missionData.transportDestination?.name,
              synchronizedDataUpdate
            );

            if (isGenericRecordDeletable(draft)) {
              deleteRecord();
            }

            onSuccess(synchronizedDataUpdate);
          },
          OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY
        );
      }
    }

    function saveSymptomOnset(onSuccess: (data: Record<string, string>) => void) {
      if (patient?.symptomOnset != null && findNodes(nodeTypeOfPage).length > 0) {
        adaptRecord(
          'generic',
          nodeTypeOfPage,
          (draft: Draft<SymptomOnsetRecord>, deleteRecord) => {
            draft.inputState = InputState.ENTERED;

            const synchronizedDataUpdate: Record<string, string> = {};
            handleUntouchedTextFieldUpdates(
              draft,
              SymptomOnsetPropertyKeys.symptomOnsetTimestamp,
              patient?.symptomOnset,
              synchronizedDataUpdate
            );

            if (isGenericRecordDeletable(draft)) {
              deleteRecord();
            }
            onSuccess(synchronizedDataUpdate);
          },
          SYMPTOM_ONSET_RECORD_ELEMENT_KEY
        );
      }
    }

    const updateLasySynchronizedMissionData = (synchronizedDataUpdate?: NormalizedMission) => {
      setLastSynchronizedMissionData((oldData) => {
        return { ...oldData, mission: { ...oldData?.mission, ...synchronizedDataUpdate } };
      });
    };

    saveParticipants(updateLasySynchronizedMissionData);
    saveMissionLocation(updateLasySynchronizedMissionData);
    saveTransportDestination(updateLasySynchronizedMissionData);
    saveSymptomOnset((synchronizedData) => {
      setLastSynchronizedBaseData((oldData) => {
        return {
          ...oldData,
          patient: {
            ...oldData?.patient,
            symptomOnset:
              synchronizedData[SymptomOnsetPropertyKeys.symptomOnsetTimestamp] || oldData?.patient?.symptomOnset,
          },
        };
      });
    });
  }, [
    missionData,
    patient,
    nodeTypeOfPage,
    setLastSynchronizedBaseData,
    adaptRecord,
    findNodes,
    setLastSynchronizedMissionData,
  ]);

  function importMissionData() {
    const hasMissionLocationDiff = hasAnyMissionDataDiff(missionDataDiff, [
      LocationPropertyKeys.missionStreetGPSLatitude,
      LocationPropertyKeys.missionCityGPSLongitude,
      LocationPropertyKeys.missionPostcode,
      LocationPropertyKeys.missionLocationType,
    ]);
    const hasTransportDestinationDiff = hasMissionDataDiff(
      missionDataDiff,
      TransportLocationPropertyKeys.transportLocation
    );

    if (hasMissionLocationDiff) {
      importMissionLocation();
    }
    if (hasTransportDestinationDiff) {
      importTransportLocation();
    }

    if (hasMissionLocationDiff || hasTransportDestinationDiff) {
      setLastSynchronizedMissionData((oldData) => {
        const newData = _.omit(normalizeMissionData(missionData), [
          'controlCenter',
          'controlCenterLicensePlate',
          'location',
          'personnelNumber',
        ]);
        return { mission: { ...oldData?.mission, ...newData } };
      });
    }
  }

  function importMissionLocation() {
    adaptRecord(
      'generic',
      nodeTypeOfPage,
      (draft: Draft<OperationalDataLocationRecord>) => {
        draft.inputState = InputState.ENTERED;

        overwriteTextField(
          draft,
          LocationPropertyKeys.missionStreetGPSLatitude,
          missionData?.missionLocation?.address?.street,
          missionDataDiff
        );
        overwriteTextField(
          draft,
          LocationPropertyKeys.missionCityGPSLongitude,
          missionData?.missionLocation?.address?.city,
          missionDataDiff
        );
        overwriteTextField(
          draft,
          LocationPropertyKeys.missionPostcode,
          missionData?.missionLocation?.address?.postalCode,
          missionDataDiff
        );
        overwriteSingleSelectField(
          draft,
          LocationPropertyKeys.missionLocationType,
          mapStringToMissionLocationKey(missionData?.missionLocation?.type),
          missionDataDiff
        );
      },
      OPERATIONAL_DATA_LOCATION_RECORD_ELEMENT_KEY
    );
  }

  function importTransportLocation() {
    adaptRecord(
      'generic',
      nodeTypeOfPage,
      (draft: Draft<OperationalDataTransportLocationRecord>) => {
        draft.inputState = InputState.ENTERED;

        overwriteTextField(
          draft,
          TransportLocationPropertyKeys.transportLocation,
          missionData?.transportDestination?.name,
          missionDataDiff
        );
      },
      OPERATIONAL_DATA_TRANSPORT_RECORD_ELEMENT_KEY
    );
  }

  function importPatient() {
    if (!hasPatientDataDiff(patientDataDiff, 'symptomOnset')) {
      return;
    }

    // translates the key value used between backend data format and record data format
    const symptomDataDiff = patientDataDiff.map((value) => {
      if (value[0] === 'symptomOnset') {
        value[0] = 'symptomOnsetTimestamp';
      }
      return value;
    });

    adaptRecord(
      'generic',
      nodeTypeOfPage,
      (draft: Draft<SymptomOnsetRecord>) => {
        draft.inputState = InputState.ENTERED;

        overwriteTextField(
          draft,
          SymptomOnsetPropertyKeys.symptomOnsetTimestamp,
          patient?.symptomOnset,
          symptomDataDiff
        );
      },
      SYMPTOM_ONSET_RECORD_ELEMENT_KEY
    );

    setLastSynchronizedBaseData((oldData) => {
      const newData = _.pick(normalizePatient(patient), ['symptomOnset']);
      return { patient: { ...oldData?.patient, ...newData } };
    });
  }

  function importChangedOperationalData() {
    importMissionData();

    importPatient();
  }

  function ignoreChangedOperationalData() {
    setLastSynchronizedBaseData((oldData) => {
      const newData = _.pick(normalizePatient(patient), ['symptomOnset']);
      return { patient: { ...oldData?.patient, ...newData } };
    });
    setLastSynchronizedMissionData((oldData) => {
      const newData = _.omit(normalizeMissionData(missionData), [
        'controlCenter',
        'controlCenterLicensePlate',
        'location',
        'personnelNumber',
      ]);
      return { mission: { ...oldData?.mission, ...newData } };
    });
  }

  const pageHeader = <PageHeader heading={getPageTitle(PageType.OperationalData)} noClearAllButton />;
  const pageFooter = <PageFooter pageType={PageType.OperationalData} diviId={DiviNavigationIds.IncidentData} />;

  return (
    <DiviPage
      pageHeader={pageHeader}
      pageFooter={pageFooter}
      recordTypes={recordTypesOfPage}
      nodeType={nodeTypeOfPage}
      elementKeys={elementKeysOfPage}
      diviId={DiviNavigationIds.IncidentData}>
      {ApplicationConfig.immediatelySyncToConference && (
        <DataImportDialog
          isDialogOpen={isImportDialogOpen}
          localizedDataDiff={localizedOperationalDataDiff}
          onConfirm={importChangedOperationalData}
          onAbort={ignoreChangedOperationalData}
        />
      )}
      <div className='legend row'>
        <div className='indicator'></div>
        <Typography variant='subtitle1'>Automatisch übertragene Daten</Typography>
      </div>
      <div className='operational-data-wireframe'>
        <div className='participants'>
          <Participants nodeType={nodeTypeOfPage} />
        </div>
        <div className='type'>
          <MissionType nodeType={nodeTypeOfPage} />
        </div>
        <div className='location'>
          <MissionLocation nodeType={nodeTypeOfPage} />
        </div>
        <div className='symptom-start'>
          <SymptomStart nodeType={nodeTypeOfPage} />
        </div>
        <div className='destination'>
          <Destination nodeType={nodeTypeOfPage} />
        </div>
      </div>
    </DiviPage>
  );
}
